export const COVERAGE_ERROR =
  "The instalment amount and tenor do not cover the financed amount.";

export function roundMoney(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function parseAmount(value) {
  return Number(String(value).replace(/,/g, ""));
}

export function formatAmountEntry(value) {
  const raw = String(value).replace(/,/g, "");
  const [integer = "", decimal] = raw.split(".");
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimal === undefined ? grouped : `${grouped}.${decimal}`;
}

export function addMonthsAnchored(dateString, offset) {
  const [year, month, day] = dateString.split("-").map(Number);
  const target = new Date(Date.UTC(year, month - 1 + offset, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)
  ).getUTCDate();
  const result = new Date(
    Date.UTC(
      target.getUTCFullYear(),
      target.getUTCMonth(),
      Math.min(day, lastDay)
    )
  );
  return result.toISOString().slice(0, 10);
}

export function formatDate(dateString) {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${dateString}T00:00:00Z`));
}

export function toDateString(date) {
  if (!date) return "";
  if (typeof date === "string") return date.slice(0, 10);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function fromDateString(dateString) {
  if (!dateString) return null;
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}

const tableMoney = new Intl.NumberFormat("en-MY", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatMoney(value) {
  return tableMoney.format(value);
}

export function buildAmortisationClipboardText(amortisation) {
  const headers = [
    "No.",
    "Due date",
    "Opening balance",
    "Principal",
    "Interest",
    "Instalment",
    "Closing balance",
  ];
  const rows = amortisation.map((row) => [
    row.period,
    formatDate(row.dueDate),
    tableMoney.format(row.opening),
    tableMoney.format(row.principal),
    tableMoney.format(row.interest),
    tableMoney.format(row.instalment),
    tableMoney.format(row.closing),
  ]);
  const totalPrincipal = amortisation
    .slice(1)
    .reduce((sum, row) => sum + row.principal, 0);
  const totalInterest = amortisation.reduce(
    (sum, row) => sum + row.interest,
    0
  );
  const totalInstalment = amortisation
    .slice(1)
    .reduce((sum, row) => sum + row.instalment, 0);
  const footer = [
    "Total payments",
    "",
    "",
    tableMoney.format(totalPrincipal),
    tableMoney.format(totalInterest),
    tableMoney.format(totalInstalment),
    "",
  ];

  return [headers, ...rows, footer].map((cells) => cells.join("\t")).join("\n");
}

export function buildCashflows({
  cost,
  deposit,
  recurringCount,
  recurringAmount,
  finalAmount,
  firstDate,
}) {
  const financed = roundMoney(cost - deposit);
  return [
    {
      period: 0,
      dueDate: addMonthsAnchored(firstDate, -1),
      description: "Amount financed",
      amount: financed,
      cost: roundMoney(cost),
      deposit: roundMoney(deposit),
      editable: false,
    },
    ...Array.from({ length: recurringCount }, (_, index) => ({
      period: index + 1,
      dueDate: addMonthsAnchored(firstDate, index),
      description: `Recurring instalment ${index + 1}`,
      amount: roundMoney(recurringAmount),
      editable: true,
    })),
    {
      period: recurringCount + 1,
      dueDate: addMonthsAnchored(firstDate, recurringCount),
      description: "Final payment",
      amount: roundMoney(finalAmount),
      editable: true,
    },
  ];
}

function npv(rate, cashflows) {
  return cashflows.reduce(
    (total, cashflow, period) => total + cashflow / (1 + rate) ** period,
    0
  );
}

export function calculateMonthlyIrr(schedule) {
  const values = schedule.map((row, index) =>
    index === 0 ? row.amount : -row.amount
  );
  const financed = values[0];
  const payments = -values.slice(1).reduce((sum, value) => sum + value, 0);

  if (financed <= 0 || payments < financed) throw new Error(COVERAGE_ERROR);
  if (Math.abs(npv(0, values)) < 1e-10) return 0;

  let low = 0;
  let high = 1;
  while (npv(high, values) < 0 && high < 1_000_000) high *= 2;
  if (npv(high, values) < 0) throw new Error(COVERAGE_ERROR);

  for (let iteration = 0; iteration < 200; iteration += 1) {
    const mid = (low + high) / 2;
    const value = npv(mid, values);
    if (Math.abs(value) < 1e-10) return mid;
    if (value < 0) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
}

export function buildAmortisation(schedule, monthlyIrr) {
  let closingBalance = 0;
  let principalPaidAfterDeposit = 0;
  const amountFinanced = roundMoney(schedule[0]?.amount ?? 0);
  const finalPaymentIndex = schedule.length - 1;

  return schedule.map((row, index) => {
    if (index === 0) {
      const opening = roundMoney(row.cost ?? row.amount);
      const principal = roundMoney(row.deposit ?? 0);
      const closing = roundMoney(opening - principal);
      closingBalance = closing;
      return {
        ...row,
        opening,
        principal,
        interest: 0,
        instalment: principal,
        closing,
      };
    }

    const opening = closingBalance;
    const isFinalPayment = index === finalPaymentIndex;
    const standardInterest = roundMoney(opening * monthlyIrr);
    const remainingPrincipal = roundMoney(
      amountFinanced - principalPaidAfterDeposit
    );
    const principal = isFinalPayment
      ? remainingPrincipal
      : roundMoney(row.amount - standardInterest);
    const interest = isFinalPayment
      ? roundMoney(row.amount - principal)
      : standardInterest;
    if (principal < 0) throw new Error(COVERAGE_ERROR);
    principalPaidAfterDeposit = roundMoney(
      principalPaidAfterDeposit + principal
    );
    closingBalance = roundMoney(opening - principal);

    return {
      ...row,
      opening,
      principal,
      interest,
      instalment: row.amount,
      closing: closingBalance,
    };
  });
}

export function validateInputs(values) {
  const errors = {};
  if (!(values.cost > 0)) errors.cost = "Enter a cost greater than 0.";
  if (!(values.deposit >= 0)) errors.deposit = "Deposit cannot be negative.";
  if (values.deposit > values.cost)
    errors.deposit = "Deposit cannot exceed the asset cost.";
  if (!Number.isInteger(values.recurringCount) || values.recurringCount <= 0)
    errors.recurringCount = "Enter a positive whole number.";
  if (!(values.recurringAmount > 0))
    errors.recurringAmount = "Enter an amount greater than 0.";
  if (!(values.finalAmount > 0))
    errors.finalAmount = "Enter an amount greater than 0.";
  if (
    !values.firstDate ||
    Number.isNaN(new Date(`${values.firstDate}T00:00:00Z`).valueOf())
  )
    errors.firstDate = "Enter a valid first instalment date.";
  return errors;
}
